import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarClock, Phone, Video, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PENDING_NOTE =
  "Your appointment request is pending. Your appointment is confirmed only after Chaplain Hall reviews availability and approves it.";

function groupSlotsByDay(slots) {
  const byDay = new Map();
  for (const s of slots) {
    const day = s.label.split(" · ")[0];
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(s);
  }
  return Array.from(byDay.entries());
}

export default function GriefAppointmentCalendar() {
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", format: "phone", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/grief/availability`);
        setAvailability(r.data || null);
        setFetchError(false);
      } catch (e) {
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => groupSlotsByDay((availability && availability.slots) || []), [availability]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selected) { setError("Please pick a date and time."); return; }
    for (const [k, label] of [["first_name", "first name"], ["last_name", "last name"], ["email", "email"], ["phone", "phone"]]) {
      if (!form[k].trim()) { setError(`Please add your ${label}.`); return; }
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/grief/appointments`, {
        ...form,
        slot_start_iso: selected.start_iso,
      });
      setConfirmation(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "slot_no_longer_available") setError("That time is no longer available. Please choose another.");
      else if (detail === "duplicate_submission") setError("A request for this time already exists on your email.");
      else if (detail === "availability_not_open") setError("Appointment requests are not open at this time.");
      else setError("We couldn't submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-3 text-slate-500" data-testid="grief-cal-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading availability…
      </div>
    );
  }

  if (fetchError || !availability) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 flex items-start gap-3" data-testid="grief-cal-error-state">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" strokeWidth={1.75} />
        <p>
          We're having trouble loading availability right now. Please refresh in
          a moment, or use the{" "}
          <a href="/contact" className="underline text-[#4A6FA5] hover:text-[#3B597B]">Contact page</a>{" "}
          to reach out.
        </p>
      </div>
    );
  }

  if (availability.enabled === false) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 flex items-start gap-3" data-testid="grief-cal-closed">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} />
        <p>Online appointment requests are currently paused. Please use the Contact page to reach out.</p>
      </div>
    );
  }

  if ((availability.slots || []).length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700 flex items-start gap-3" data-testid="grief-cal-no-slots-state">
        <CalendarClock className="w-4 h-4 shrink-0 mt-0.5 text-[#4A6FA5]" strokeWidth={1.75} />
        <p>
          There are currently no available appointment times. Please check back
          or use the{" "}
          <a href="/contact" className="underline text-[#4A6FA5] hover:text-[#3B597B]">Contact page</a>{" "}
          to reach out.
        </p>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="grief-cal-success">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" strokeWidth={1.75} />
          <div>
            <p className="font-semibold text-emerald-900">Request received.</p>
            <p className="mt-2 text-sm text-emerald-900 leading-relaxed">{PENDING_NOTE}</p>
            <p className="mt-3 text-xs text-emerald-800">All times shown in <strong>{availability.timezone}</strong>.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="mt-16 md:mt-20" data-testid="grief-cal-section" aria-labelledby="grief-cal-heading">
      <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">Request an Appointment</p>
      <h2 id="grief-cal-heading" className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
        Choose a time that works for you
      </h2>
      <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl">
        All appointment times are shown in <strong>{availability.timezone}</strong>. {PENDING_NOTE}
      </p>

      {/* Slot picker */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid="grief-cal-slots">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="w-4 h-4 text-[#4A6FA5]" strokeWidth={1.75} />
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">Available dates and times</p>
        </div>
        {grouped.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="grief-cal-no-slots">No open times right now. Please check back soon.</p>
        )}
        <div className="grid gap-4">
          {grouped.map(([day, slotsOfDay]) => (
            <div key={day}>
              <p className="text-sm font-semibold text-slate-800 mb-2">{day}</p>
              <div className="flex flex-wrap gap-2">
                {slotsOfDay.map((s) => {
                  const isSel = selected && selected.start_iso === s.start_iso;
                  const time = s.label.split(" · ")[1] || s.label;
                  return (
                    <button
                      key={s.start_iso}
                      type="button"
                      onClick={() => setSelected(s)}
                      data-testid={`grief-cal-slot-${s.start_iso}`}
                      className={`px-3 py-2 rounded-lg text-sm border transition ${
                        isSel
                          ? "border-[#4A6FA5] bg-[#4A6FA5] text-white shadow-sm"
                          : "border-slate-200 bg-white text-slate-800 hover:border-[#4A6FA5]"
                      }`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid="grief-cal-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-600">First name</span>
            <input required value={form.first_name} onChange={set("first_name")} data-testid="grief-cal-input-first" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:border-[#4A6FA5]" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-600">Last name</span>
            <input required value={form.last_name} onChange={set("last_name")} data-testid="grief-cal-input-last" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:border-[#4A6FA5]" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-600">Email</span>
            <input type="email" required value={form.email} onChange={set("email")} data-testid="grief-cal-input-email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:border-[#4A6FA5]" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-600">Phone</span>
            <input required value={form.phone} onChange={set("phone")} data-testid="grief-cal-input-phone" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:border-[#4A6FA5]" />
          </label>
        </div>

        <fieldset className="mt-5">
          <legend className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-2">Preferred format</legend>
          <div className="flex gap-3">
            {[
              { v: "phone", label: "Phone", Icon: Phone },
              { v: "zoom", label: "Zoom", Icon: Video },
            ].map(({ v, label, Icon }) => (
              <label key={v} data-testid={`grief-cal-format-${v}`} className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 cursor-pointer text-sm ${form.format === v ? "border-[#4A6FA5] bg-[#F0F4F8] text-slate-900" : "border-slate-300 text-slate-700"}`}>
                <input type="radio" name="format" value={v} checked={form.format === v} onChange={set("format")} className="sr-only" />
                <Icon className="w-4 h-4 text-[#4A6FA5]" strokeWidth={1.75} /> {label}
              </label>
            ))}
          </div>
        </fieldset>

        <p className="mt-5 text-xs text-slate-500 leading-relaxed">
          Please do not include sensitive personal or medical details in this request. {PENDING_NOTE}
        </p>

        {error && (
          <p className="mt-4 text-sm text-red-700" data-testid="grief-cal-error">{error}</p>
        )}

        <div className="mt-5">
          <button
            type="submit"
            disabled={submitting}
            data-testid="grief-cal-submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Request an Appointment
          </button>
          {selected && (
            <p className="mt-3 text-xs text-slate-500">Selected: <strong>{selected.label}</strong> · {availability.timezone}</p>
          )}
        </div>
      </form>
    </section>
  );
}
